import { SeededRandom, coordSeed } from '../utils/random.js';
import { STAR_CLASSES, PLANET_TYPES, BASE_NOTES, HARMONY, PLANET_GENRES, SYSTEM_MUSICAL_STYLES, SYSTEM_STYLE_NAMES } from '../utils/constants.js';

/**
 * Galaxy Generator – creates deterministic star systems from coordinates.
 * The galaxy is infinite; systems are generated on-demand from their coordinates.
 */
export class GalaxyGenerator {
  constructor(universeSeed = 42) {
    this.universeSeed = universeSeed;
    this.systemCache = new Map();
  }

  /**
   * Get or generate a star system at galaxy-grid coordinates (gx, gy).
   * Each grid cell is ~10 light-years across.
   */
  getSystem(gx, gy) {
    const key = `${gx},${gy}`;
    if (this.systemCache.has(key)) return this.systemCache.get(key);

    const seed = coordSeed(gx, gy, this.universeSeed);
    const rng = new SeededRandom(seed);

    // ~40% chance a grid cell has a star system
    if (rng.next() > 0.4) {
      this.systemCache.set(key, null);
      return null;
    }

    const system = this._generateSystem(gx, gy, rng);
    this.systemCache.set(key, system);
    return system;
  }

  /** Generate star systems in a region around (cx, cy) with radius r */
  getRegion(cx, cy, radius) {
    const systems = [];
    for (let x = cx - radius; x <= cx + radius; x++) {
      for (let y = cy - radius; y <= cy + radius; y++) {
        const sys = this.getSystem(x, y);
        if (sys) systems.push(sys);
      }
    }
    return systems;
  }

  _generateSystem(gx, gy, rng) {
    const starClass = STAR_CLASSES[rng.int(0, STAR_CLASSES.length - 1)];
    const starSize = rng.range(starClass.size[0], starClass.size[1]);
    const numPlanets = rng.int(1, 7);

    const systemName = this._generateName(rng);

    // ── System-level musical identity ──
    // Each solar system gets a time signature, BPM bias, and genre preferences
    const systemStyleName = rng.pick(SYSTEM_STYLE_NAMES);
    const systemStyle = SYSTEM_MUSICAL_STYLES[systemStyleName];
    const systemTimeSig = systemStyle.timeSig;

    const planets = [];
    for (let i = 0; i < numPlanets; i++) {
      planets.push(this._generatePlanet(rng, i, numPlanets, starClass, systemStyleName, systemTimeSig));
    }

    return {
      gx, gy,
      name: systemName,
      seed: coordSeed(gx, gy, this.universeSeed),
      star: {
        class: starClass.type,
        color: starClass.color,
        size: starSize,
        temp: starClass.temp,
      },
      planets,
      // System musical identity
      musicalStyle: systemStyleName,
      timeSig: systemTimeSig,
      // Position offset within the grid cell for visual variety
      offset: { x: rng.range(-0.3, 0.3), y: rng.range(-0.3, 0.3) },
    };
  }

  _generatePlanet(rng, index, total, starClass, systemStyleName, systemTimeSig) {
    const type = rng.pick(PLANET_TYPES);
    const orbitRadius = 30 + index * rng.range(15, 35);
    const size = rng.range(0.6, 1.6);
    const orbitSpeed = 0.1 / (index + 1);
    const orbitPhase = rng.range(0, Math.PI * 2);

    // ── Genre selection — weighted by system style ──
    const systemStyle = SYSTEM_MUSICAL_STYLES[systemStyleName];
    const genreWeights = systemStyle.genreWeights;
    const genre = this._weightedPick(rng, genreWeights);
    const genreCfg = PLANET_GENRES[genre];

    // Musical signature — genre-aware scale and tempo selection
    const genreScales = HARMONY.GENRE_SCALES[genre] || HARMONY.PLEASANT_SCALES;
    const scaleName = rng.pick(genreScales);
    const rootNote = rng.pick(BASE_NOTES);
    const octave = rng.int(2, 4);
    // Tempo from genre range, with some per-planet variation
    const tempo = Math.round(rng.range(genreCfg.tempoRange[0], genreCfg.tempoRange[1]));
    const numCreatures = rng.int(12, 30);

    // Visual properties
    const hue = rng.next();
    const saturation = rng.range(0.3, 0.9);
    const lightness = rng.range(0.3, 0.7);
    const hasAtmosphere = rng.next() > 0.3;
    const atmosphereHue = rng.next();
    const hasRings = rng.next() > 0.8;
    const ringColor = rng.next();

    // Terrain
    const terrainOctaves = rng.int(3, 6);
    const terrainScale = rng.range(0.5, 3);
    const terrainHeight = rng.range(0.05, 0.22);

    return {
      index,
      name: this._generatePlanetName(rng),
      type,
      orbitRadius,
      orbitSpeed,
      orbitPhase,
      size,
      seed: rng.int(0, 999999),
      music: {
        scale: scaleName,
        root: rootNote,
        octave,
        tempo,
        reverb: genreCfg.reverbMix + rng.range(-0.1, 0.1),
        drone: genreCfg.droneLevel + rng.range(-0.1, 0.1),
        genre,
        timeSig: systemTimeSig,
        swing: genreCfg.swingAmount,
        filterCutoff: genreCfg.filterCutoff,
      },
      visual: {
        hue, saturation, lightness,
        hasAtmosphere, atmosphereHue,
        hasRings, ringColor,
      },
      terrain: {
        octaves: terrainOctaves,
        scale: terrainScale,
        height: terrainHeight,
      },
      numCreatures,
      claimed: false,
      claimedBy: null,
      claimedName: null,
    };
  }

  _generateName(rng) {
    const prefixes = ['Alpha', 'Beta', 'Gamma', 'Delta', 'Sigma', 'Omega', 'Nova', 'Astra', 'Nebula', 'Vega', 'Orion', 'Lyra', 'Cygnus', 'Draco', 'Aquila', 'Perseus'];
    const suffixes = ['Prime', 'Major', 'Minor', 'Proxima', 'Ultima', 'Centauri', 'Reticuli', 'Eridani'];
    const prefix = rng.pick(prefixes);
    const useSuffix = rng.next() > 0.5;
    const num = rng.int(1, 999);
    return useSuffix ? `${prefix} ${rng.pick(suffixes)}` : `${prefix}-${num}`;
  }

  _generatePlanetName(rng) {
    const syllables = ['ar', 'en', 'ix', 'on', 'um', 'al', 'is', 'or', 'us', 'el', 'an', 'ith', 'yn', 'ol', 'ur', 'ax', 'em', 'io', 'za', 'ka', 'ri', 'ta', 'lu', 'vo', 'se', 'mi', 'no', 'pho'];
    const count = rng.int(2, 4);
    let name = '';
    for (let i = 0; i < count; i++) {
      name += rng.pick(syllables);
    }
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  /**
   * Pick a key from a {key: weight} object using weighted random selection.
   */
  _weightedPick(rng, weights) {
    const entries = Object.entries(weights);
    const total = entries.reduce((sum, [, w]) => sum + w, 0);
    let roll = rng.next() * total;
    for (const [key, w] of entries) {
      roll -= w;
      if (roll <= 0) return key;
    }
    return entries[entries.length - 1][0];
  }
}
